import type { Vero1ChecklistItem } from "shared/interfaces/api/vero1";

import { isAgentTouched } from "./itemState";

export interface ClearedEntry {
  itemId: Vero1ChecklistItem["id"];
  label: string;
  applicantName?: string | null;
  findings: string;
  internalNote: string;
  actionForLeasing: string;
}

/**
 * Rows this refresh reset that the analyst had worked.
 *
 * The response reports counts, not identities, and `changedVsPriorRun` is also
 * set at generation time — so neither says what THIS refresh cleared. The
 * before/after pair does: a row the analyst had touched that comes back
 * untouched was reset, and a heal leaves entries in place so it never matches.
 */
export const clearedByRefresh = (
  before: Vero1ChecklistItem[] | undefined,
  after: Vero1ChecklistItem[] | undefined
): ClearedEntry[] => {
  if (!before?.length || !after?.length) return [];
  const now = new Map(after.map((item) => [String(item.id), item]));

  return before.reduce<ClearedEntry[]>((cleared, prior) => {
    const current = now.get(String(prior.id));
    if (!current || !isAgentTouched(prior) || isAgentTouched(current)) {
      return cleared;
    }
    return cleared.concat({
      itemId: prior.id,
      label: prior.label,
      applicantName: prior.applicantName,
      findings: prior.overrideExplanation || "",
      internalNote: prior.internalNote || "",
      actionForLeasing: prior.actionForLeasing || "",
    });
  }, []);
};

export const clearedRowNames = (entries: ClearedEntry[]) =>
  entries
    .map((entry) =>
      entry.applicantName
        ? `${entry.label} — ${entry.applicantName}`
        : entry.label
    )
    .join(", ");

const storageKey = (checklistId: Vero1ChecklistItem["id"]) =>
  `vero1-cleared:${checklistId}`;

/**
 * The cleared text exists nowhere but this browser — the API never returns
 * what it discarded — so an undismissed notice must survive a page reload.
 * sessionStorage scopes that survival to the tab: the note text never
 * outlives the session it was captured in.
 */
export const storeClearedEntries = (
  checklistId: Vero1ChecklistItem["id"],
  entries: ClearedEntry[]
) => {
  try {
    if (entries.length === 0) {
      window.sessionStorage.removeItem(storageKey(checklistId));
    } else {
      window.sessionStorage.setItem(
        storageKey(checklistId),
        JSON.stringify(entries)
      );
    }
  } catch {
    // storage full or unavailable — the in-memory notice still renders
  }
};

export const readClearedEntries = (
  checklistId: Vero1ChecklistItem["id"] | null
): ClearedEntry[] => {
  if (checklistId === null) return [];
  try {
    const raw = window.sessionStorage.getItem(storageKey(checklistId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
